import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import { useState } from "react";
import { StatusPill, Icon, PersonAvatar } from "../../../mui";
import { brainTokens } from "../../../design/brainTheme";
import { freshnessLabel, type FreshnessBand } from "../../../lib/library-freshness";
import type { LibraryDocRow } from "../../../lib/brain-api";

const c = brainTokens.color;

const TYPE_TONE: Record<string, "neutral" | "teal" | "success" | "warning"> = {
  sop: "teal",
  runbook: "warning",
  "how-to": "success",
  overview: "neutral",
};

export function LibraryRow({
  doc,
  onOpen,
  onPin,
  onArchive,
  canEdit,
  hero,
}: {
  doc: LibraryDocRow;
  onOpen: () => void;
  onPin?: () => void;
  onArchive?: () => void;
  canEdit?: boolean;
  hero?: boolean;
}) {
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const band = (doc.freshness ?? "unknown") as FreshnessBand;
  const ageColor =
    band === "stale" ? c.warnText : band === "aging" ? c.warnText : c.muted2;

  return (
    <Box
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: hero ? 2 : 1.5,
        py: hero ? 1.75 : 1.25,
        border: `1px solid ${hero ? c.tealTintBorder : c.borderLight}`,
        background: hero ? c.tealTint2 : c.pageBg,
        borderRadius: `${brainTokens.radius.card}px`,
        cursor: "pointer",
        minWidth: 0,
        "&:hover": { borderColor: c.hoverBorder },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
          <Box sx={{ fontSize: hero ? 15.5 : 14, fontWeight: 600, color: c.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {doc.title}
          </Box>
          <StatusPill
            tone={TYPE_TONE[doc.docType] ?? "neutral"}
            label={doc.docType === "how-to" ? "How-to" : doc.docType.toUpperCase()}
          />
          {doc.docKey.endsWith(".html") && <StatusPill tone="neutral" label="HTML" />}
          {(doc.tags ?? []).slice(0, 4).map((t) => (
            <Box
              key={t}
              sx={{
                fontSize: 11,
                color: c.muted2,
                border: `1px solid ${c.borderLight}`,
                borderRadius: "999px",
                px: 1,
                py: "1px",
              }}
            >
              {t}
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, fontSize: 12, color: ageColor }}>
          {doc.owner && (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, color: c.muted2 }}>
              <PersonAvatar name={doc.owner} size={18} />
              {doc.owner}
            </Box>
          )}
          <Box component="span">{freshnessLabel(band, doc.verifiedAt)}</Box>
        </Box>
      </Box>
      {canEdit && (
        <>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setMenuEl(e.currentTarget);
            }}
            aria-label="Doc actions"
          >
            <Icon name="chevron-down" size={16} />
          </IconButton>
          <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={() => setMenuEl(null)}>
            {onPin && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuEl(null);
                  onPin();
                }}
              >
                {doc.pinned ? "Unpin" : "Pin as Start here"}
              </MenuItem>
            )}
            {onArchive && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuEl(null);
                  onArchive();
                }}
              >
                Archive
              </MenuItem>
            )}
          </Menu>
        </>
      )}
    </Box>
  );
}
